import React, { useState, useEffect } from 'react';
import '../styles/CargasTable.css';

const CargasTable = ({ planSeleccionado, onCargasChange, initialCargas = [] }) => {
  const [cargas, setCargas] = useState([]);
  
  // Función para obtener el número de cargas según el plan
  const getCargasCount = (plan) => {
    switch (plan) {
      case 'titular-sin-cargas':
        return 0;
      case 'titular-1-carga':
        return 1;
      case 'titular-2-cargas':
        return 2;
      case 'titular-3-cargas':
        return 3;
      case 'titular-4-mas-cargas':
        return 4; // Mínimo 4, puede agregar más
      default:
        return 0;
    }
  };

  // Función para validar RUT
  const validateRUT = (rut) => {
    if (!rut.trim()) return true; // Permitir vacío
    
    const cleanRut = rut.replace(/\./g, '').replace('-', '');
    if (cleanRut.length < 8 || cleanRut.length > 9) return false;
    
    const rutBody = cleanRut.slice(0, -1);
    const dv = cleanRut.slice(-1).toLowerCase();
    
    let sum = 0;
    let multiplier = 2;
    
    for (let i = rutBody.length - 1; i >= 0; i--) {
      sum += parseInt(rutBody[i]) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    
    const remainder = sum % 11;
    const dvCalculated = 11 - remainder;
    let calculatedDv;
    
    if (dvCalculated === 11) {
      calculatedDv = '0';
    } else if (dvCalculated === 10) {
      calculatedDv = 'k';
    } else {
      calculatedDv = dvCalculated.toString();
    }
    
    return dv === calculatedDv;
  };

  // Función para formatear RUT
  const formatRUT = (value) => {
    if (!value) return '';
    
    const cleanValue = value.replace(/[^0-9kK]/g, '');
    
    if (cleanValue.length <= 1) {
      return cleanValue.replace(/[kK]/g, '');
    }
    
    const rutBody = cleanValue.slice(0, -1).replace(/[kK]/g, '');
    const dv = cleanValue.slice(-1);
    
    const formattedBody = rutBody.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${formattedBody}-${dv}`;
  };

  // Inicializar cargas según el plan seleccionado
  useEffect(() => {
    const cargasCount = getCargasCount(planSeleccionado);
    
    if (cargasCount === 0) {
      setCargas([]);
      onCargasChange([]);
      return;
    }

    // Si hay cargas iniciales, usarlas, sino crear vacías
    if (initialCargas.length > 0) {
      setCargas(initialCargas);
    } else {
      const newCargas = Array.from({ length: cargasCount }, (_, index) => ({
        id: index + 1,
        rut: '',
        nombres: '',
        fechaNacimiento: '',
        errors: {}
      }));
      setCargas(newCargas);
      onCargasChange(newCargas);
    }
  }, [planSeleccionado]);

  // Manejar cambios en los campos
  const handleFieldChange = (cargaId, field, value) => {
    let processedValue = value;
    
    // Formatear RUT si es necesario
    if (field === 'rut') {
      processedValue = formatRUT(value);
    }

    const updatedCargas = cargas.map(carga => {
      if (carga.id === cargaId) {
        const updatedCarga = { 
          ...carga, 
          [field]: processedValue,
          errors: { ...carga.errors }
        };

        // Validar RUT si se modificó
        if (field === 'rut') {
          if (processedValue && !validateRUT(processedValue)) {
            updatedCarga.errors.rut = 'RUT inválido';
          } else {
            delete updatedCarga.errors.rut;
          }
        }

        return updatedCarga;
      }
      return carga;
    });

    setCargas(updatedCargas);
    onCargasChange(updatedCargas);
  };

  // Agregar fila adicional (solo para plan 4 o más cargas)
  const agregarFila = () => {
    const newId = Math.max(...cargas.map(c => c.id), 0) + 1;
    const newCarga = {
      id: newId,
      rut: '',
      nombres: '',
      fechaNacimiento: '',
      errors: {}
    };
    
    const updatedCargas = [...cargas, newCarga];
    setCargas(updatedCargas);
    onCargasChange(updatedCargas);
  };

  // Eliminar fila (solo las adicionales en plan 4+)
  const eliminarFila = (cargaId) => {
    const minCargas = getCargasCount(planSeleccionado);
    if (cargas.length <= minCargas) return;

    const updatedCargas = cargas.filter(carga => carga.id !== cargaId);
    setCargas(updatedCargas);
    onCargasChange(updatedCargas);
  };

  // Validar si una carga está completamente vacía
  const isCargaEmpty = (carga) => {
    return !carga.rut.trim() && !carga.nombres.trim() && !carga.fechaNacimiento.trim();
  };

  // Validar si una carga está completamente llena
  const isCargaComplete = (carga) => {
    return carga.rut.trim() && carga.nombres.trim() && carga.fechaNacimiento.trim();
  };

  if (getCargasCount(planSeleccionado) === 0) {
    return null;
  }

  const minCargas = getCargasCount(planSeleccionado);
  const canAddMore = planSeleccionado === 'titular-4-mas-cargas';

  return (
    <div className="cargas-section">
      <h3>Datos de las Cargas</h3>
      <p className="cargas-info">
        {canAddMore 
          ? `Ingrese los datos de sus cargas (mínimo ${minCargas}). Puede dejar cargas en blanco.`
          : `Ingrese los datos de sus ${minCargas} carga${minCargas > 1 ? 's' : ''}. Puede dejar cargas en blanco.`
        }
      </p>

      <div className="cargas-table-container">
        <table className="cargas-table">
          <thead>
            <tr>
              <th>Carga</th>
              <th>RUT</th>
              <th>Nombres Completos</th>
              <th>Fecha Nacimiento</th>
              {canAddMore && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {cargas.map((carga, index) => (
              <tr key={carga.id} className={isCargaEmpty(carga) ? 'empty-row' : ''}>
                <td className="carga-number">
                  {index + 1}
                </td>
                <td>
                  <input
                    type="text"
                    value={carga.rut}
                    onChange={(e) => handleFieldChange(carga.id, 'rut', e.target.value)}
                    placeholder="12.345.678-9"
                    className={`table-input ${carga.errors.rut ? 'error' : ''}`}
                    maxLength="12"
                  />
                  {carga.errors.rut && <span className="error-message">{carga.errors.rut}</span>}
                </td>
                <td>
                  <input
                    type="text"
                    value={carga.nombres}
                    onChange={(e) => handleFieldChange(carga.id, 'nombres', e.target.value)}
                    placeholder="Nombres completos"
                    className="table-input"
                  />
                </td>
                <td>
                  <input
                    type="date"
                    value={carga.fechaNacimiento}
                    onChange={(e) => handleFieldChange(carga.id, 'fechaNacimiento', e.target.value)}
                    className="table-input"
                  />
                </td>
                {canAddMore && (
                  <td>
                    {index >= minCargas && (
                      <button
                        type="button"
                        onClick={() => eliminarFila(carga.id)}
                        className="remove-btn"
                        title="Eliminar carga"
                      >
                        ×
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {canAddMore && (
          <div className="add-carga-container">
            <button
              type="button"
              onClick={agregarFila}
              className="add-carga-btn"
            >
              + Agregar carga adicional
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CargasTable;